/**
 * Breadcrumb navigation component.
 * Automatically generates breadcrumbs based on current route.
 */

import { FC } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import { NavigateNext, Home as HomeIcon } from '@mui/icons-material';

// Map of route paths to display names
const breadcrumbNameMap: Record<string, string> = {
  '/': 'Dashboard',
  '/vlans': 'VLANs',
  '/interfaces': 'Interfaces',
  '/switches': 'Switches',
  '/connectivity': 'Connectivity Check',
  '/transactions': 'Transaction History',
  '/settings': 'Settings',
};

/**
 * Format a breadcrumb value for display.
 * Handles both route names and dynamic parameters.
 */
const formatBreadcrumb = (value: string): string => {
  // Check if it's a known route
  const route = `/${value}`;
  if (breadcrumbNameMap[route]) {
    return breadcrumbNameMap[route];
  }

  // For dynamic values (IDs, hostnames, etc.), capitalize first letter
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const Breadcrumbs: FC = () => {
  const location = useLocation();

  // Split pathname and filter out empty strings
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
      >
        {/* Home link */}
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.primary',
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>

        {/* Dynamic breadcrumbs based on path */}
        {pathnames.map((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isLast = index === pathnames.length - 1;

          return isLast ? (
            <Typography key={to} color="text.primary" fontWeight="medium">
              {formatBreadcrumb(value)}
            </Typography>
          ) : (
            <Link
              key={to}
              component={RouterLink}
              to={to}
              underline="hover"
              sx={{
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              {formatBreadcrumb(value)}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};
